// Control Plan Editor Page - AIAG-VDA Step 7 Results Documentation
// Full CRUD with auto-generation from PFMEA detection controls
// ============================================================

import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    ClipboardCheck,
    Sparkles,
    Plus,
    Download,
    FileSpreadsheet,
    Trash2,
    Save,
    Search,
    Link2,
    AlertTriangle,
    Printer,
} from 'lucide-react';
import { useTheme } from '../contexts/ThemeContext';
import { useAuth } from '../contexts/AuthContext';
import { useAppContext } from '../contexts/AppContext';
import clsx from 'clsx';
import { getPfmeaItems } from '../services/pfmeaService';
import { generateControlPlanFromPfmea, extractProcessStepsFromFlow } from '../services/analysisService';
import { getProcessFlows, SavedProcessFlow } from '../services/processFlowService';
import { ControlPlanItem, PfmeaItem, ProcessStep } from '../types';
import ControlPlanTable from '../components/ControlPlan/ControlPlanTable';
import { ModalWrapper } from '../components/ModalWrapper';

const PrintableControlPlan: React.FC<{ items: ControlPlanItem[], project: string }> = ({ items, project }) => {
    return (
        <div className="hidden print:block font-sans text-black bg-white p-8">
            <div className="mb-6 pb-4 border-b-2 border-black flex justify-between items-center">
                <div>
                    <h1 className="text-2xl font-bold uppercase tracking-tight">Control Plan</h1>
                    <div className="text-sm mt-1 text-gray-600">Project: <span className="font-semibold text-black">{project}</span></div>
                </div>
                <div className="text-right text-xs">
                    <div>Generated: <span className="font-semibold">{new Date().toLocaleDateString()}</span></div>
                    <div>Total Items: <span className="font-semibold">{items.length}</span></div>
                </div>
            </div>

            <table className="w-full text-[10px] text-left border-collapse">
                <thead>
                    <tr className="border-b-2 border-black bg-gray-50">
                        <th className="py-2 pl-2 border-r border-gray-300">Step</th>
                        <th className="py-2 pl-2 border-r border-gray-300">Process Name / Operation</th>
                        <th className="py-2 pl-2 border-r border-gray-300">Machine</th>
                        <th className="py-2 pl-2 border-r border-gray-300">Characteristic</th>
                        <th className="py-2 pl-2 border-r border-gray-300">Spec/Tol</th>
                        <th className="py-2 pl-2 border-r border-gray-300">Eval Tech</th>
                        <th className="py-2 pl-2 border-r border-gray-300">Sample</th>
                        <th className="py-2 pl-2 border-r border-gray-300">Control Method</th>
                        <th className="py-2 pl-2">Reaction Plan</th>
                    </tr>
                </thead>
                <tbody>
                    {items.map((item, idx) => (
                        <tr key={idx} className="border-b border-gray-200 break-inside-avoid">
                            <td className="py-2 pl-2 align-top font-mono border-r border-gray-300">{item.processStepNumber}</td>
                            <td className="py-2 pl-2 align-top border-r border-gray-300 font-semibold">{item.processStepName}</td>
                            <td className="py-2 pl-2 align-top border-r border-gray-300">{item.machine || ''}</td>
                            <td className="py-2 pl-2 align-top border-r border-gray-300">
                                <div className="font-medium">{item.characteristic}</div>
                                <div className="text-[9px] text-gray-500 mt-0.5">
                                    {item.specialCharacteristic !== 'none' && (
                                        <span className={clsx(
                                            "px-1 py-0.5 rounded border mr-1",
                                            item.specialCharacteristic === 'CC' ? "border-red-500 text-red-700 font-bold" : "border-amber-500 text-amber-700 font-bold"
                                        )}>{item.specialCharacteristic}</span>
                                    )}
                                    {item.characteristicType}
                                </div>
                            </td>
                            <td className="py-2 pl-2 align-top border-r border-gray-300">{item.specification}</td>
                            <td className="py-2 pl-2 align-top border-r border-gray-300">{item.evaluationTechnique}</td>
                            <td className="py-2 pl-2 align-top border-r border-gray-300">
                                {item.sampleSize}<br /><span className="text-gray-500">{item.sampleFrequency}</span>
                            </td>
                            <td className="py-2 pl-2 align-top border-r border-gray-300">{item.controlMethod}</td>
                            <td className="py-2 pl-2 align-top italic text-gray-600">{item.reactionPlan}</td>
                        </tr>
                    ))}
                </tbody>
            </table>

            <div className="mt-8 pt-4 border-t border-gray-300 text-[10px] text-gray-500 flex justify-between">
                <span>Magnus PFMEA System</span>
                <span>Confidential - For Internal Use Only</span>
            </div>
        </div>
    );
};

const ControlPlanPage: React.FC = () => {
    const { theme } = useTheme();
    const { hasPermission } = useAuth();
    const { activeProject, activeProjectName } = useAppContext();
    const navigate = useNavigate();
    const isDark = theme === 'dark';

    // State
    const [items, setItems] = useState<ControlPlanItem[]>([]);
    const [pfmeaItems, setPfmeaItems] = useState<PfmeaItem[]>([]);
    const [loading, setLoading] = useState(true);
    const [isGenerating, setIsGenerating] = useState(false);
    const [showSelectionModal, setShowSelectionModal] = useState(false);
    const [availableFlows, setAvailableFlows] = useState<SavedProcessFlow[]>([]);
    const [selectedFlowId, setSelectedFlowId] = useState<string>('');
    const [flowSteps, setFlowSteps] = useState<ProcessStep[]>([]);
    const [selectedStepIds, setSelectedStepIds] = useState<Set<string>>(new Set());
    const [isLoadingFlows, setIsLoadingFlows] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const [selectedItems, setSelectedItems] = useState<Set<string>>(new Set());

    const [hasChanges, setHasChanges] = useState(false);
    const [showGenerateModal, setShowGenerateModal] = useState(false);

    const loadData = useCallback(async () => {
        if (!activeProjectName) return;
        setLoading(true);
        try {
            // Load PFMEA items for linking and generation
            const pfmeaData = await getPfmeaItems(activeProjectName);
            setPfmeaItems(pfmeaData);
            // For now, start with empty control plan (would load from backend)
            setItems([]);
        } catch (err: unknown) {
            console.error('Error loading data:', err);
            setPfmeaItems([]);
        } finally {
            setLoading(false);
        }
    }, [activeProjectName]);

    // Load data
    useEffect(() => {
        if (activeProjectName) {
            loadData();
        }
    }, [activeProjectName, loadData]);

    // Load process flows for the active project
    useEffect(() => {
        const loadFlows = async () => {
            if (activeProjectName) {
                setIsLoadingFlows(true);
                try {
                    const flows = await getProcessFlows(activeProjectName);
                    setAvailableFlows(flows);
                } catch (error) {
                    console.error('Failed to load process flows:', error);
                } finally {
                    setIsLoadingFlows(false);
                }
            }
        };
        loadFlows();
    }, [activeProjectName]);

    // Handle flow selection
    const handleFlowChange = async (flowId: string) => {
        setSelectedFlowId(flowId);
        if (!flowId) {
            setFlowSteps([]);
            return;
        }

        const flow = availableFlows.find(f => f.id === flowId);
        if (flow) {
            const steps = extractProcessStepsFromFlow(flow.flow.nodes, activeProjectName || '');
            setFlowSteps(steps);
            // Default select all steps
            setSelectedStepIds(new Set(steps.map(s => s.id)));
        }
    };

    const handleToggleStep = (stepId: string) => {
        const newSelected = new Set(selectedStepIds);
        if (newSelected.has(stepId)) {
            newSelected.delete(stepId);
        } else {
            newSelected.add(stepId);
        }
        setSelectedStepIds(newSelected);
    };

    const handleGenerateFromFlow = () => {
        const selectedSteps = flowSteps.filter(s => selectedStepIds.has(s.id));

        const newItems: ControlPlanItem[] = selectedSteps.map(step => ({
            id: `cp-${Date.now()}-${Math.random().toString(36).substr(2, 5)}`,
            processStepNumber: step.stepNumber,
            processStepName: step.stepName,
            machine: step.equipment || '',
            characteristic: step.characteristic || 'Process Operation',
            characteristicType: 'process',
            specialCharacteristic: (step.specialCharacteristics && step.specialCharacteristics.length > 0) ? (step.specialCharacteristics[0] as 'CC' | 'SC' | 'none') : 'none',
            specification: step.specification || step.description || '',
            evaluationTechnique: step.controlMethod || 'Visual / Operator Check',
            sampleSize: step.sampleSize || '100%',
            sampleFrequency: step.sampleFrequency || 'Continuous',
            controlMethod: step.controlMethod || 'Standard Work',
            reactionPlan: 'Stop, Notify Supervisor',
        }));

        setItems(prev => [...prev, ...newItems]);
        setShowSelectionModal(false);
        setSelectedFlowId('');
        setFlowSteps([]);
    };

    // Generate Control Plan from PFMEA detection controls
    const handleGenerateFromPfmea = useCallback(() => {
        setIsGenerating(true);

        const generatedItems = generateControlPlanFromPfmea(pfmeaItems);

        // Add to existing items, avoiding duplicates if they have the same linkedPfmeaId
        const existingLinkedIds = new Set(items.map(i => i.linkedPfmeaId).filter(Boolean));
        const newItems = generatedItems.filter(i => !existingLinkedIds.has(i.linkedPfmeaId));

        if (newItems.length > 0) {
            setItems(prev => [...prev, ...newItems]);
            setHasChanges(true);
        }

        setIsGenerating(false);
        setShowGenerateModal(false);
    }, [pfmeaItems, items]);

    // Add new item
    const handleAddItem = () => {
        const newItem: ControlPlanItem = {
            id: `CP-${Date.now()}`,
            processStepNumber: '',
            processStepName: '',
            machine: '',
            characteristic: '',
            characteristicType: 'process',
            specialCharacteristic: 'none',
            specification: '',
            evaluationTechnique: '',
            sampleSize: 'n=5',
            sampleFrequency: 'Every shift',
            controlMethod: '',
            reactionPlan: '',
        };
        setItems([...items, newItem]);

        setHasChanges(true);
    };

    // Update item
    const handleUpdateItem = (id: string, field: keyof ControlPlanItem, value: ControlPlanItem[keyof ControlPlanItem]) => {
        setItems(items.map(item =>
            item.id === id ? { ...item, [field]: value } : item
        ));
        setHasChanges(true);
    };

    // Delete selected items
    const handleDeleteSelected = () => {
        if (!confirm(`Delete ${selectedItems.size} selected items?`)) return;
        setItems(items.filter(item => !selectedItems.has(item.id)));
        setSelectedItems(new Set());
        setHasChanges(true);
    };

    // Toggle selection
    const toggleSelection = (id: string) => {
        const next = new Set(selectedItems);
        if (next.has(id)) {
            next.delete(id);
        } else {
            next.add(id);
        }
        setSelectedItems(next);
    };

    // Filter items
    const filteredItems = useMemo(() => {
        if (!searchTerm) return items;
        const term = searchTerm.toLowerCase();
        return items.filter(item =>
            item.processStepNumber.toLowerCase().includes(term) ||
            item.processStepName.toLowerCase().includes(term) ||
            item.characteristic.toLowerCase().includes(term) ||
            item.controlMethod.toLowerCase().includes(term)
        );
    }, [items, searchTerm]);

    // Stats
    const stats = useMemo(() => ({
        total: items.length,
        cc: items.filter(i => i.specialCharacteristic === 'CC').length,
        sc: items.filter(i => i.specialCharacteristic === 'SC').length,
        linked: items.filter(i => i.linkedPfmeaId).length,
    }), [items]);

    // Export to CSV (AIAG format)
    const handleExportCSV = () => {
        const headers = [
            'Step #',
            'Process Step',
            'Machine',
            'Characteristic',
            'Type',
            'CC/SC',
            'Specification',
            'Evaluation Method',
            'Sample Size',
            'Sample Frequency',
            'Control Method',
            'Reaction Plan',
        ];

        const csvContent = [
            headers.join(','),
            ...items.map(item => [
                item.processStepNumber,
                `"${item.processStepName.replace(/"/g, '""')}"`,
                `"${(item.machine || '').replace(/"/g, '""')}"`,
                `"${item.characteristic.replace(/"/g, '""')}"`,
                item.characteristicType,
                item.specialCharacteristic === 'none' ? '' : item.specialCharacteristic,
                `"${item.specification.replace(/"/g, '""')}"`,
                `"${item.evaluationTechnique.replace(/"/g, '""')}"`,
                item.sampleSize,
                item.sampleFrequency,
                `"${item.controlMethod.replace(/"/g, '""')}"`,
                `"${item.reactionPlan.replace(/"/g, '""')}"`,
            ].join(','))
        ].join('\n');

        const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `Control_Plan_${activeProjectName || 'export'}_${new Date().toISOString().split('T')[0]}.csv`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
    };

    // No project selected
    if (!activeProject || activeProject === 'Select Project') {
        return (
            <div className="flex flex-col items-center justify-center h-96 text-center">
                <ClipboardCheck size={64} className="text-slate-300 mb-4" />
                <h2 className={clsx('text-xl font-semibold mb-2', isDark ? 'text-white' : 'text-slate-800')}>
                    No Project Selected
                </h2>
                <p className="text-slate-500 mb-4">Select a project to view the Control Plan.</p>
                <button
                    onClick={() => navigate('/projects')}
                    className="px-4 py-2 bg-primary-500 text-white rounded-lg hover:bg-primary-600"
                >
                    Go to Projects
                </button>
            </div>
        );
    }

    return (
        <>
            <div className="space-y-6 print:hidden">
                {/* Header */}
                <div className="flex items-center justify-between flex-wrap gap-4">
                    <div>
                        <h1 className={clsx('text-2xl font-bold flex items-center gap-2', isDark ? 'text-white' : 'text-slate-800')}>
                            <ClipboardCheck className="text-green-500" />
                            Control Plan
                        </h1>
                        <p className="text-slate-500 text-sm">
                            AIAG-VDA Step 7: Results Documentation · {activeProject}
                        </p>
                    </div>
                    <div className="flex gap-2">
                        {hasChanges && hasPermission('control_plan.edit') && (
                            <button
                                onClick={() => {
                                    // Would save to backend
                                    setHasChanges(false);
                                    alert('Control Plan saved!');
                                }}
                                className="flex items-center gap-2 px-4 py-2 bg-green-500 text-white rounded-lg hover:bg-green-600"
                            >
                                <Save size={16} />
                                Save Changes
                            </button>
                        )}
                        <button
                            onClick={() => setShowSelectionModal(true)}
                            className="flex items-center gap-2 px-4 py-2 bg-purple-500/10 text-purple-500 rounded-lg hover:bg-purple-500/20 transition-all border border-purple-500/20 font-medium"
                        >
                            <Link2 size={18} />
                            Load from Flow
                        </button>

                        <button
                            onClick={handleGenerateFromPfmea}
                            disabled={isGenerating}
                            className="flex items-center gap-2 px-4 py-2 bg-primary-500 text-white rounded-lg hover:bg-primary-600 transition-all shadow-lg shadow-primary-500/20 disabled:opacity-50 font-medium"
                        >
                            <Sparkles size={18} />
                            {isGenerating ? 'Generating...' : 'Generate from PFMEA'}
                        </button>
                        <button
                            onClick={() => window.print()}
                            disabled={items.length === 0}
                            className="flex items-center gap-2 px-4 py-2 bg-slate-100 dark:bg-slate-700 rounded-lg hover:bg-slate-200 dark:hover:bg-slate-600 disabled:opacity-50"
                        >
                            <Printer size={16} />
                            Print
                        </button>

                        <button
                            onClick={handleExportCSV}
                            disabled={items.length === 0}
                            className="flex items-center gap-2 px-4 py-2 bg-slate-100 dark:bg-slate-700 rounded-lg hover:bg-slate-200 dark:hover:bg-slate-600 disabled:opacity-50"
                        >
                            <Download size={16} />
                            Export CSV
                        </button>
                        {hasPermission('control_plan.create') && (
                            <button
                                onClick={() => setShowGenerateModal(true)}
                                disabled={pfmeaItems.length === 0}
                                className="flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-green-500 to-emerald-500 text-white rounded-lg hover:opacity-90 disabled:opacity-50"
                            >
                                <Sparkles size={16} />
                                Generate from PFMEA
                            </button>
                        )}
                    </div>
                </div>

                {/* Stats Cards */}
                <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                    <div className={clsx('p-4 rounded-xl border', isDark ? 'bg-slate-800 border-slate-700' : 'bg-white border-slate-200')}>
                        <div className="flex items-center gap-3">
                            <div className="p-2 rounded-lg bg-green-100 dark:bg-green-900">
                                <ClipboardCheck size={20} className="text-green-600 dark:text-green-400" />
                            </div>
                            <div>
                                <p className="text-2xl font-bold text-slate-800 dark:text-white">{stats.total}</p>
                                <p className="text-sm text-slate-500">Total Items</p>
                            </div>
                        </div>
                    </div>
                    <div className={clsx('p-4 rounded-xl border', isDark ? 'bg-red-900/30 border-red-800' : 'bg-red-50 border-red-200')}>
                        <div className="flex items-center gap-3">
                            <div className="p-2 rounded-lg bg-red-100 dark:bg-red-900">
                                <AlertTriangle size={20} className="text-red-600 dark:text-red-400" />
                            </div>
                            <div>
                                <p className="text-2xl font-bold text-red-700 dark:text-red-300">{stats.cc}</p>
                                <p className="text-sm text-red-600 dark:text-red-400">Critical (CC)</p>
                            </div>
                        </div>
                    </div>
                    <div className={clsx('p-4 rounded-xl border', isDark ? 'bg-amber-900/30 border-amber-800' : 'bg-amber-50 border-amber-200')}>
                        <div className="flex items-center gap-3">
                            <div className="p-2 rounded-lg bg-amber-100 dark:bg-amber-900">
                                <AlertTriangle size={20} className="text-amber-600 dark:text-amber-400" />
                            </div>
                            <div>
                                <p className="text-2xl font-bold text-amber-700 dark:text-amber-300">{stats.sc}</p>
                                <p className="text-sm text-amber-600 dark:text-amber-400">Significant (SC)</p>
                            </div>
                        </div>
                    </div>
                    <div className={clsx('p-4 rounded-xl border', isDark ? 'bg-blue-900/30 border-blue-800' : 'bg-blue-50 border-blue-200')}>
                        <div className="flex items-center gap-3">
                            <div className="p-2 rounded-lg bg-blue-100 dark:bg-blue-900">
                                <Link2 size={20} className="text-blue-600 dark:text-blue-400" />
                            </div>
                            <div>
                                <p className="text-2xl font-bold text-blue-700 dark:text-blue-300">{stats.linked}</p>
                                <p className="text-sm text-blue-600 dark:text-blue-400">Linked to PFMEA</p>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Toolbar */}
                <div className={clsx(
                    'p-4 rounded-xl border flex flex-wrap items-center justify-between gap-4',
                    isDark ? 'bg-slate-800 border-slate-700' : 'bg-white border-slate-200'
                )}>
                    <div className="flex items-center gap-2">
                        {hasPermission('control_plan.create') && (
                            <button
                                onClick={handleAddItem}
                                className="flex items-center gap-2 px-3 py-2 text-sm bg-blue-500 text-white rounded-lg hover:bg-blue-600"
                            >
                                <Plus size={16} />
                                Add Item
                            </button>
                        )}
                        {selectedItems.size > 0 && hasPermission('control_plan.edit') && (
                            <button
                                onClick={handleDeleteSelected}
                                className="flex items-center gap-2 px-3 py-2 text-sm bg-red-100 text-red-600 rounded-lg hover:bg-red-200"
                            >
                                <Trash2 size={16} />
                                Delete ({selectedItems.size})
                            </button>
                        )}
                    </div>
                    <div className="flex items-center gap-2">
                        <div className="relative">
                            <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                            <input
                                type="text"
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                placeholder="Search..."
                                className={clsx(
                                    'pl-9 pr-4 py-2 rounded-lg border text-sm w-64',
                                    isDark ? 'bg-slate-700 border-slate-600 text-white' : 'bg-white border-slate-200'
                                )}
                            />
                        </div>
                    </div>
                </div>

                {/* Table */}
                <div className={clsx('rounded-xl border overflow-hidden', isDark ? 'border-slate-700' : 'border-slate-200')}>
                    {loading ? (
                        <div className="p-12 text-center text-slate-500">Loading...</div>
                    ) : items.length === 0 ? (
                        <div className={clsx('p-12 text-center', isDark ? 'bg-slate-900' : 'bg-white')}>
                            <ClipboardCheck size={48} className="mx-auto text-slate-300 mb-4" />
                            <p className={clsx('text-lg font-medium', isDark ? 'text-white' : 'text-slate-800')}>
                                No Control Plan Items
                            </p>
                            <p className="text-sm text-slate-500 mt-1 max-w-md mx-auto">
                                {pfmeaItems.length > 0
                                    ? `You have ${pfmeaItems.length} PFMEA items. Generate a Control Plan from the detection controls.`
                                    : 'Create PFMEA items first, then generate a Control Plan.'}
                            </p>
                            <div className="flex justify-center gap-4 mt-4">
                                {pfmeaItems.length > 0 ? (
                                    <button
                                        onClick={() => setShowGenerateModal(true)}
                                        className="px-4 py-2 bg-gradient-to-r from-green-500 to-emerald-500 text-white rounded-lg hover:opacity-90"
                                    >
                                        <Sparkles size={16} className="inline mr-2" />
                                        Generate from PFMEA
                                    </button>
                                ) : (
                                    <button
                                        onClick={() => navigate('/pfmea-editor')}
                                        className="px-4 py-2 bg-primary-500 text-white rounded-lg hover:bg-primary-600"
                                    >
                                        <FileSpreadsheet size={16} className="inline mr-2" />
                                        Go to PFMEA Editor
                                    </button>
                                )}
                            </div>
                        </div>
                    ) : (
                        <ControlPlanTable
                            items={filteredItems}
                            isDark={isDark}
                            selectedItems={selectedItems}
                            toggleSelection={toggleSelection}
                            setSelectedItems={setSelectedItems}
                            handleUpdateItem={handleUpdateItem as any}
                        />
                    )}
                </div>

                {/* Sample Frequency Guide */}
                <div className={clsx('p-4 rounded-xl border', isDark ? 'bg-green-900/20 border-green-800' : 'bg-green-50 border-green-200')}>
                    <h3 className="font-semibold text-green-700 dark:text-green-300 mb-2">📊 Sample Frequency Guidelines (Based on Severity)</h3>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm text-green-600 dark:text-green-400">
                        <div>
                            <p className="font-medium">Severity ≥ 9</p>
                            <p>100% Inspection</p>
                        </div>
                        <div>
                            <p className="font-medium">Severity 7-8</p>
                            <p>n=5 every hour</p>
                        </div>
                        <div>
                            <p className="font-medium">Severity 5-6</p>
                            <p>n=5 every shift</p>
                        </div>
                        <div>
                            <p className="font-medium">Severity &lt; 5</p>
                            <p>First/last piece</p>
                        </div>
                    </div>
                </div>

                {/* Generate Modal */}
                {showGenerateModal && (
                    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
                        <div className={clsx('w-full max-w-lg rounded-xl p-6', isDark ? 'bg-slate-800' : 'bg-white')}>
                            <h2 className={clsx('text-xl font-bold mb-4', isDark ? 'text-white' : 'text-slate-800')}>
                                Generate Control Plan from PFMEA
                            </h2>

                            <div className={clsx('p-4 rounded-lg mb-4', isDark ? 'bg-slate-700' : 'bg-slate-50')}>
                                <p className="text-sm text-slate-600 dark:text-slate-300 mb-2">
                                    This will create Control Plan items from your PFMEA detection controls:
                                </p>
                                <ul className="text-sm text-slate-500 list-disc list-inside space-y-1">
                                    <li>{pfmeaItems.filter(i => i.actionPriority === 'H').length} High priority items</li>
                                    <li>{pfmeaItems.filter(i => i.actionPriority === 'M').length} Medium priority items</li>
                                    <li>Sample sizes based on severity rating</li>
                                    <li>Detection controls → Evaluation techniques</li>
                                </ul>
                            </div>

                            <div className="flex gap-3">
                                <button
                                    onClick={() => setShowGenerateModal(false)}
                                    className="flex-1 px-4 py-2 text-sm border border-slate-200 dark:border-slate-600 rounded-lg hover:bg-slate-50 dark:hover:bg-slate-700"
                                >
                                    Cancel
                                </button>
                                <button
                                    onClick={handleGenerateFromPfmea}
                                    disabled={isGenerating}
                                    className="flex-1 px-4 py-2 text-sm bg-gradient-to-r from-green-500 to-emerald-500 text-white rounded-lg hover:opacity-90 disabled:opacity-50"
                                >
                                    {isGenerating ? 'Generating...' : 'Generate Control Plan'}
                                </button>
                            </div>
                        </div>
                    </div>
                )}
            </div>
            <PrintableControlPlan items={filteredItems} project={activeProjectName || ''} />
            {/* Process Flow Selection Modal */}
            <ModalWrapper
                isOpen={showSelectionModal}
                onClose={() => setShowSelectionModal(false)}
                title="Load Process Steps from Flow Chart"
                maxWidth="max-w-2xl"
            >
                <div className="space-y-6">
                    <div>
                        <label className="block text-sm font-semibold text-[var(--text-secondary)] mb-2">
                            Select Process Flow
                        </label>
                        <select
                            value={selectedFlowId}
                            onChange={(e) => handleFlowChange(e.target.value)}
                            className="w-full bg-[var(--bg-primary)] border border-[var(--border-color)] rounded-lg p-3 text-[var(--text-primary)] focus:ring-2 focus:ring-primary-500 outline-none transition-all"
                        >
                            <option value="">-- Choose a Process Flow --</option>
                            {availableFlows.map(flow => (
                                <option key={flow.id} value={flow.id}>{flow.name}</option>
                            ))}
                        </select>
                        {availableFlows.length === 0 && !isLoadingFlows && (
                            <p className="mt-2 text-xs text-amber-500 flex items-center gap-1">
                                <AlertTriangle size={12} />
                                No process flows found for this project.
                            </p>
                        )}
                    </div>

                    {flowSteps.length > 0 && (
                        <div className="space-y-4">
                            <div className="flex items-center justify-between">
                                <h3 className="text-sm font-bold text-[var(--text-primary)]">
                                    Select Steps to Import ({selectedStepIds.size} selected)
                                </h3>
                                <div className="flex gap-2">
                                    <button
                                        onClick={() => setSelectedStepIds(new Set(flowSteps.map(s => s.id)))}
                                        className="text-xs text-primary-500 hover:underline"
                                    >
                                        Select All
                                    </button>
                                    <button
                                        onClick={() => setSelectedStepIds(new Set())}
                                        className="text-xs text-[var(--text-muted)] hover:underline"
                                    >
                                        Deselect All
                                    </button>
                                </div>
                            </div>

                            <div className="max-h-64 overflow-y-auto border border-[var(--border-color)] rounded-lg divide-y divide-[var(--border-color)]">
                                {flowSteps.map(step => (
                                    <div
                                        key={step.id}
                                        className="flex items-center gap-3 p-3 hover:bg-[var(--bg-primary)]/50 transition-colors cursor-pointer"
                                        onClick={() => handleToggleStep(step.id)}
                                    >
                                        <input
                                            type="checkbox"
                                            checked={selectedStepIds.has(step.id)}
                                            onChange={() => { }} // Handled by div click
                                            className="rounded border-[var(--border-color)] text-primary-500 w-4 h-4"
                                        />
                                        <div className="flex-1 min-w-0">
                                            <div className="flex items-center gap-2">
                                                <span className="text-[10px] font-mono bg-[var(--bg-primary)] px-1.5 py-0.5 rounded border border-[var(--border-color)] text-[var(--text-secondary)]">
                                                    {step.stepNumber}
                                                </span>
                                                <span className="text-sm font-medium text-[var(--text-primary)] truncate">
                                                    {step.stepName}
                                                </span>
                                            </div>
                                            {step.description && (
                                                <p className="text-xs text-[var(--text-muted)] truncate mt-0.5">
                                                    {step.description}
                                                </p>
                                            )}
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}

                    <div className="flex justify-end gap-3 pt-4 border-t border-[var(--border-color)]">
                        <button
                            onClick={() => setShowSelectionModal(false)}
                            className="px-4 py-2 text-[var(--text-secondary)] hover:bg-[var(--bg-primary)] rounded-lg transition-colors text-sm font-medium"
                        >
                            Cancel
                        </button>
                        <button
                            onClick={handleGenerateFromFlow}
                            disabled={!selectedFlowId || selectedStepIds.size === 0}
                            className="px-6 py-2 bg-primary-500 text-white rounded-lg hover:bg-primary-600 transition-colors text-sm font-bold shadow-lg shadow-primary-500/20 disabled:opacity-50"
                        >
                            Import Selected Steps
                        </button>
                    </div>
                </div>
            </ModalWrapper>
        </>
    );
};

export default ControlPlanPage;
